/**
 * The Doorly AFM™ Underwriting Guide — the RENDERER. Used by:
 *   - /underwriting               (UnderwritingPortal, Guidelines tab)
 *   - /admin/underwriting-guide  (AdminLayout chrome, print + edit-back buttons)
 *   - /market/portal/resources/underwriting-guide  (InvestorPortalLayout chrome)
 *
 * 🔑 THE GUIDE IS NOT IN THIS FILE (Jesse, 2026-10-21). Its text is built on the server from the AFM matrix
 * (backend/constants/uwGuideDocument.js) and served by GET /api/uw-matrix/guide only to staff, partners with an
 * approved agreement and approved DoorlyMarket investors (backend/middleware/guideAccess). This file renders whatever
 * structure arrives — sections of headings, paragraphs, bullets, tables and callouts — and ships no guide prose in the
 * public bundle. Until the document arrives the page says it is loading; a viewer without access is told to sign in.
 */
import React, { useState, useEffect } from 'react';
import { fetchGuide } from '../utils/guideAuth';
import NdaModal from './market/NdaModal';

export const SectionHeader = ({ id, children, allowCopy = true }) => {
  const onCopyLink = (e) => {
    e.preventDefault();
    const url = `${window.location.origin}${window.location.pathname}#${id}`;
    navigator.clipboard.writeText(url).catch(() => {});
    e.currentTarget.title = 'Copied!';
    setTimeout(() => { if (e.currentTarget) e.currentTarget.title = 'Copy link to section'; }, 2000);
  };
  return (
    <h2 id={id} style={{
      fontSize: 18, fontWeight: 700, color: '#111111',
      borderBottom: '2px solid #3a6ad9', paddingBottom: 8, marginTop: 32, marginBottom: 12,
      display: 'flex', alignItems: 'center', gap: 8, scrollMarginTop: 88,
    }}>
      {children}
      {allowCopy && (
        <button onClick={onCopyLink} title="Copy link to section"
          className="uw-copy-link"
          style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#9ca3af', fontSize: 13, padding: '2px 6px', opacity: 0, transition: 'opacity 0.15s' }}>
          🔗
        </button>
      )}
    </h2>
  );
};

export const SubHeader = ({ children }) => (
  <h3 style={{ fontSize: 15, fontWeight: 600, color: '#3a6ad9', marginTop: 18, marginBottom: 8 }}>
    {children}
  </h3>
);

export const Body = ({ children, style }) => (
  <p style={{ fontSize: 14, lineHeight: 1.7, color: '#374151', marginTop: 0, marginBottom: 10, ...style }}>
    {children}
  </p>
);

export const Bullets = ({ items }) => (
  <ul style={{ fontSize: 14, lineHeight: 1.6, color: '#374151', marginTop: 4, marginBottom: 10, paddingLeft: 22 }}>
    {items.filter((it) => it != null && it !== false && it !== '').map((it, i) => <li key={i} style={{ marginBottom: 4 }}>{it}</li>)}
  </ul>
);

export const Callout = ({ children, tone = 'info' }) => {
  const palette = tone === 'warning'
    ? { bg: '#ffe6e9', accent: '#ff082f' }
    : tone === 'success'
      ? { bg: '#f0fde4', accent: '#3a6ad9' }
      : { bg: '#eff6ff', accent: '#3a6ad9' };
  return (
    <div style={{
      background: palette.bg, borderLeft: `4px solid ${palette.accent}`,
      borderRadius: 6, padding: '12px 16px', margin: '12px 0',
      fontSize: 13, color: '#374151', lineHeight: 1.6,
    }}>
      {children}
    </div>
  );
};

export const DataTable = ({ headers, rows }) => {
  const minTableWidth = headers.length > 4 ? 720 : headers.length > 2 ? 480 : 360;
  return (
    <div style={{ marginBottom: 14 }}>
      <div className="scroll-x">
        <table style={{ width: '100%', minWidth: minTableWidth, borderCollapse: 'collapse', fontSize: 13 }}>
          <thead>
            <tr>
              {headers.map((h) => (
                <th key={h} style={{ background: '#111111', color: '#ffffff', padding: '10px 12px', textAlign: 'left', fontWeight: 700, fontSize: 12, textTransform: 'uppercase', letterSpacing: 0.04, whiteSpace: 'nowrap' }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((r, i) => (
              <tr key={i} style={{ background: i % 2 === 0 ? '#ffffff' : '#f9fafb', borderTop: '1px solid #e5e7eb' }}>
                {r.map((c, j) => (
                  <td key={j} style={{ padding: '8px 12px', color: '#374151' }}>{c}</td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="scroll-hint">← Scroll to see more →</div>
    </div>
  );
};

// ── formatting helpers for pages that render served values ──
export const pctOf = (v) => (v == null || Number.isNaN(Number(v)) ? '—' : `${Math.round(Number(v) * 1000) / 10}%`);
export const usdOf = (n) => (n == null ? '—' : `$${Number(n).toLocaleString('en-US')}`);

/** The text of one of the matrix's derogatory / credit rows, from the served matrix. */
export const derogRuleText = (matrix, event) => (((matrix && matrix.derogSeasoning) || []).find((r) => r[0] === event) || [])[1] || '';

// ── 🔑 ONE AUTHORIZED FETCH PER PAGE, SHARED. The table of contents and the body read the same request. ──
const cache = {};
/** Forget every answer that was not a 200, so the next render asks again (after the password page unlocks). */
export function resetGuideCache() {
  for (const k of Object.keys(cache)) if (!cache[k].result || cache[k].result.status !== 200) delete cache[k];
}
function useServed(path) {
  const [state, setState] = useState(() => (cache[path] && cache[path].result) || { data: null, status: null });
  useEffect(() => {
    let alive = true;
    if (!cache[path] || cache[path].result?.status !== 200) {
      cache[path] = { promise: fetchGuide(path).then((r) => { cache[path].result = { data: r.data || null, status: r.status, code: r.code || null }; return cache[path].result; }) };
    }
    cache[path].promise.then((res) => { if (alive) setState(res); });
    return () => { alive = false; };
  }, [path]);
  return state;
}

/** The guide document: { doc, status } — status 200, 401 (not signed in), 403 (no access), 0 (network), null (loading). */
export function useGuideDocument() {
  const { data, status, code } = useServed('/api/uw-matrix/guide');
  return { doc: data, status, code };
}
/** The confidential portal material (About, Projections): staff and NDA-signed investors only. */
export function usePortalConfidential() {
  const { data, status, code } = useServed('/api/uw-matrix/portal-confidential');
  return { content: data, status, code };
}
/** The section list for a table of contents ([] until the document arrives). */
export function useGuideSections() {
  const { doc } = useGuideDocument();
  return (doc && doc.sections) || [];
}
/** The full matrix payload (authorized viewers). */
export function useUWMatrixState() {
  const { data, status } = useServed('/api/uw-matrix');
  return { matrix: data, failed: status != null && status !== 200, status };
}
export function useUWMatrix() {
  return useUWMatrixState().matrix;
}

/** "Version 2 · policy 2026.10.3, effective 2026-10-21" — the line every surface shows at the top. */
export function GuideVersionLine({ matrix, style }) {
  if (!matrix) return null;
  return (
    <div data-testid="uw-guide-version" style={{ fontSize: 13, color: '#374151', ...style }}>
      <strong>Doorly AFM™ Underwriting Guidelines — Version {matrix.edition}</strong>
      {' · '}policy {matrix.version}, effective {matrix.effective}
    </div>
  );
}

/** Inline **bold** in served text. */
function Rich({ text }) {
  const parts = String(text == null ? '' : text).split(/(\*\*[^*]+\*\*)/g);
  return <>{parts.map((s, i) => (/^\*\*[^*]+\*\*$/.test(s) ? <strong key={i}>{s.slice(2, -2)}</strong> : <React.Fragment key={i}>{s}</React.Fragment>))}</>;
}

function Block({ b, doc }) {
  switch (b.t) {
    case 'version': return <Callout><GuideVersionLine matrix={doc} /><div style={{ marginTop: 4 }}>Every figure on this page is served from the AFM matrix the engine decides with; the Program Matrix and these Guidelines cannot state different numbers.</div></Callout>;
    case 'h3': return <SubHeader><Rich text={b.text} /></SubHeader>;
    case 'p': return <Body><Rich text={b.text} /></Body>;
    case 'ul': return <Bullets items={(b.items || []).map((x, i) => <Rich key={i} text={x} />)} />;
    case 'table': return <DataTable headers={b.headers || []} rows={(b.rows || []).map((r) => r.map((c, i) => <Rich key={i} text={c} />))} />;
    case 'callout': return <Callout tone={b.tone}><Rich text={b.text} /></Callout>;
    case 'labels': return <div hidden data-testid={b.testid}>{b.text}</div>;
    default: return null;
  }
}

const LINK = { color: '#3a6ad9', fontWeight: 600, textDecoration: 'underline' };

/** An approved investor who has not signed the DoorlyMarket NDA: the prompt, and the click-through NDA itself. */
export function NdaPrompt() {
  const [open, setOpen] = useState(false);
  let token = null;
  try { token = localStorage.getItem('doorly_investor_token'); } catch (_) { token = null; }
  return (
    <div data-testid="uw-guide-nda-required" style={{ padding: '24px 4px', fontSize: 14, color: '#374151', lineHeight: 1.7 }}>
      The underwriting guidelines and Doorly&rsquo;s confidential materials are available once you have signed the
      DoorlyMarket Non-Disclosure Agreement.
      <div style={{ marginTop: 12 }}>
        <button type="button" data-testid="uw-sign-nda" onClick={() => setOpen(true)}
          style={{ padding: '10px 16px', borderRadius: 10, border: 'none', background: '#3a6ad9', color: '#fff', fontWeight: 700, fontSize: 14, cursor: 'pointer' }}>
          Review and sign the NDA
        </button>
      </div>
      {open && <NdaModal token={token} onSigned={() => { setOpen(false); window.location.reload(); }} />}
    </div>
  );
}

/** What a viewer without access sees instead of the guide. */
export function GuideSignIn({ status, code }) {
  if (code === 'NDA_REQUIRED') return <NdaPrompt />;
  return (
    <div data-testid={status === 403 ? 'uw-guide-forbidden' : 'uw-guide-signin'} style={{ padding: '32px 4px', fontSize: 14, color: '#374151', lineHeight: 1.7 }}>
      {status === 403
        ? 'Your account does not have access to the underwriting guidelines. Partners have access once their agreement is signed and their account is active; investors once approved.'
        : 'The underwriting guidelines are available to Doorly staff, partners and approved investors. Sign in to view them:'}
      {status !== 403 && (
        <ul style={{ marginTop: 8, paddingLeft: 22 }}>
          <li><a style={LINK} href="/admin/login">Doorly staff — DoorlyDesk sign-in</a></li>
          <li><a style={LINK} href="/doorlypro/login">Partners — DoorlyPro sign-in</a></li>
          <li><a style={LINK} href="/market/login">Investors — DoorlyMarket sign-in</a></li>
        </ul>
      )}
    </div>
  );
}

export default function UWGuideBody({ allowCopyLinks = true }) {
  const { doc, status, code } = useGuideDocument();
  if (status === 401 || status === 403) return <GuideSignIn status={status} code={code} />;
  if (!doc) {
    return (
      <div data-testid={status != null && status !== 200 ? 'uw-guide-unavailable' : 'uw-guide-loading'} style={{ padding: '32px 4px', fontSize: 14, color: '#6b7280' }}>
        {status != null && status !== 200
          ? 'The underwriting guidelines could not be loaded. Refresh the page to try again.'
          : 'Loading the underwriting guidelines…'}
      </div>
    );
  }
  return (
    <>
      {doc.sections.map((s) => (
        <section key={s.id} className="uw-section">
          <SectionHeader id={s.id} allowCopy={allowCopyLinks}>{s.number}. {s.label}</SectionHeader>
          {s.blocks.map((b, i) => <Block key={i} b={b} doc={doc} />)}
        </section>
      ))}
    </>
  );
}
